import { useCallback, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock,
  Eye,
  FileText,
  Loader2,
  MapPin,
  Radio,
  Sparkles,
  TrendingUp,
  UserPlus,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import DataEntryGuidanceChip from "@/components/DataEntryGuidanceChip";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import { toInsightLines } from "@/utils/insightActionPlanDefaults";
import {
  buildDataEntryDefaultPriorityActions,
  buildDataEntryDefaultRecommendations,
  buildDataEntryDefaultSignals,
  buildDataEntryExecutiveSummary,
  buildDataEntryInsightTimeline,
  buildDataEntryWatchlist,
  fmt,
  pct,
} from "@/utils/dataEntryDerived";

const SIGNAL_ICONS = ["⌁", "⌬", "↻", "◎"];

const SummaryLine = ({ children }) => (
  <li dangerouslySetInnerHTML={{ __html: children }} />
);

const buildSummaryHtml = (overview, criticalCount) =>
  buildDataEntryExecutiveSummary(overview, criticalCount).map((line) =>
    line
      .replace(/(\d[\d,%.]*%?)/g, "<b>$1</b>")
      .replace(/(<b>)+/g, "<b>")
      .replace(/(<\/b>)+/g, "</b>")
  );

const KpiTrend = ({ value, max = 100 }) => {
  const heightPct = Math.min(100, max > 0 ? (value / max) * 100 : 0);
  return (
    <div className="de-insights-kpi-trend" aria-hidden="true">
      <div className="de-insights-kpi-trend-fill" style={{ height: `${heightPct}%` }} />
    </div>
  );
};

const formatGeneratedAt = (iso) => {
  if (!iso) return "Not generated yet";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "Not generated yet";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function DataEntryInsightsTab({
  overview,
  criticalCount: criticalCountProp = 0,
  blockData = [],
  scopeLabel = "Pune District",
  guidance,
  guidanceProvider = "local",
  guidanceLoading = false,
  guidanceGeneratedAt,
  onRefreshGuidance,
}) {
  const [refreshing, setRefreshing] = useState(false);
  const criticalCount = criticalCountProp;

  const derivedSignals = useMemo(
    () => buildDataEntryDefaultSignals(overview, criticalCount, blockData),
    [overview, criticalCount, blockData]
  );

  const derivedRecommendations = useMemo(
    () => buildDataEntryDefaultRecommendations(overview, criticalCount, blockData),
    [overview, criticalCount, blockData]
  );

  const derivedPriorityActions = useMemo(
    () => buildDataEntryDefaultPriorityActions(overview, criticalCount),
    [overview, criticalCount]
  );

  const derivedTimeline = useMemo(
    () => buildDataEntryInsightTimeline(overview, criticalCount, blockData),
    [overview, criticalCount, blockData]
  );

  const signals = guidance?.signals?.length ? guidance.signals : derivedSignals;
  const recommendations = guidance?.recommendations?.length ? guidance.recommendations : derivedRecommendations;
  const priorityActions = guidance?.priority_actions?.length ? guidance.priority_actions : derivedPriorityActions;

  const defaultRoot = useMemo(() => toInsightLines(derivedSignals), [derivedSignals]);
  const defaultRecs = useMemo(() => toInsightLines(derivedRecommendations), [derivedRecommendations]);
  const defaultActions = useMemo(() => toInsightLines(derivedPriorityActions), [derivedPriorityActions]);
  const aiContent = guidance?.markdown || guidance?.ai_markdown || "";
  const timeline = guidance?.timeline?.length ? guidance.timeline : derivedTimeline;

  const watchlist = useMemo(
    () =>
      guidance?.watchlist?.length
        ? guidance.watchlist
        : buildDataEntryWatchlist(overview, criticalCount),
    [guidance?.watchlist, overview, criticalCount]
  );

  const summaryLines = useMemo(() => {
    if (guidance?.executive_summary?.length) {
      return guidance.executive_summary.map((line) =>
        String(line)
          .replace(/(\d[\d,%.]*%?)/g, "<b>$1</b>")
          .replace(/(<b>)+/g, "<b>")
          .replace(/(<\/b>)+/g, "</b>")
      );
    }
    return buildSummaryHtml(overview, criticalCount);
  }, [guidance?.executive_summary, overview, criticalCount]);

  const generatedLabel = formatGeneratedAt(guidanceGeneratedAt);
  const providerLabel = guidanceProvider === "mistral" ? "Mistral" : "Rule-based";

  const handleGenerate = useCallback(async () => {
    if (!onRefreshGuidance) return;
    setRefreshing(true);
    try {
      await onRefreshGuidance();
      toast.success(
        guidanceProvider === "mistral"
          ? "Insights refreshed with Mistral AI."
          : "Insights refreshed (rule-based fallback)."
      );
    } catch {
      toast.success("Insights refreshed (rule-based fallback).");
    } finally {
      setRefreshing(false);
    }
  }, [onRefreshGuidance, guidanceProvider]);

  const loading = guidanceLoading || refreshing;

  if (!overview) {
    return (
      <div className="de-insights-empty">
        <Sparkles size={40} className="de-insights-empty-icon" />
        <p>No insights data available for the selected scope.</p>
      </div>
    );
  }

  const certRate = pct(overview.certification_rate ?? 0, 1);

  return (
    <div className="de-insights-tab">
      <section className="de-insights-command-card">
        <div className="de-insights-command-main">
          <div className="de-insights-ai-orb">
            <Wand2 size={28} />
          </div>
          <div>
            <h2 className="dashboard-kpi-hero-title">Data Entry Status Insights</h2>
            <p>
              Mistral-powered insights, root cause signals, recommendations, and priority actions for data entry status.
            </p>
          </div>
        </div>

        <div className="de-insights-command-actions">
          <button type="button" className="de-insights-generate-btn" onClick={handleGenerate} disabled={loading}>
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
            {loading ? "Generating..." : "Refresh Insights"}
          </button>

          <div className="de-insights-meta-strip">
            <div className="de-insights-meta-pill">
              <Clock size={18} className="text-slate-500" />
              <span>
                <small>Last generated:</small>
                <b>{generatedLabel}</b>
              </span>
            </div>
            <div className="de-insights-meta-pill">
              <MapPin size={18} className="text-slate-500" />
              <span>
                <small>Scope:</small>
                <b>{scopeLabel}</b>
              </span>
            </div>
            <div className="de-insights-meta-pill">
              <DataEntryGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
            </div>
            <div className="de-insights-meta-pill">
              <Wand2 size={18} className="text-violet-500" />
              <span>
                <small>Model:</small>
                <b>{providerLabel}</b>
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="de-insights-kpi-grid" aria-label="Insight KPIs">
        <article className="de-insights-kpi-card green">
          <div className="de-insights-kpi-icon">
            <TrendingUp size={26} />
          </div>
          <div>
            <small>Completion Rate</small>
            <strong>{pct(overview.completion_rate ?? 0, 2)}</strong>
            <span>
              Across <b>{fmt(overview.total_students ?? 0)}</b> students
            </span>
          </div>
          <KpiTrend value={overview.completion_rate ?? 0} />
        </article>

        <article className="de-insights-kpi-card orange">
          <div className="de-insights-kpi-icon">
            <UserPlus size={26} />
          </div>
          <div>
            <small>Pending Students</small>
            <strong>{fmt(overview.pending_students ?? 0)}</strong>
            <span>{pct(overview.pending_rate ?? 0, 2)} of total students</span>
          </div>
          <KpiTrend value={overview.pending_rate ?? 0} max={5} />
        </article>

        <article className="de-insights-kpi-card">
          <div className="de-insights-kpi-icon">
            <CheckCircle2 size={26} />
          </div>
          <div>
            <small>Certified Schools</small>
            <strong>
              {fmt(overview.certified_schools ?? 0)}{" "}
              <span className="de-insights-kpi-inline">({certRate})</span>
            </strong>
            <span>Schools certified</span>
          </div>
          <KpiTrend value={overview.certification_rate ?? 0} />
        </article>

        <article className="de-insights-kpi-card red">
          <div className="de-insights-kpi-icon">
            <AlertTriangle size={26} />
          </div>
          <div>
            <small>Critical Schools (&lt;95%)</small>
            <strong>{fmt(criticalCount)}</strong>
            <span>Require immediate attention</span>
          </div>
          <KpiTrend
            value={overview.total_schools ? (criticalCount / overview.total_schools) * 100 : 0}
            max={5}
          />
        </article>
      </section>

      <section className="de-insights-two-col">
        <article className="de-insights-panel de-insights-summary-card">
          <div className="de-insights-panel-head">
            <div className="de-insights-panel-title">
              <span className="de-insights-mini-icon">
                <FileText size={18} />
              </span>
              <h3>Executive Summary</h3>
            </div>
          </div>
          <ul className="de-insights-summary-list">
            {summaryLines.map((line, idx) => (
              <SummaryLine key={idx}>{line}</SummaryLine>
            ))}
          </ul>
        </article>

        <article className="de-insights-panel">
          <div className="de-insights-panel-head">
            <div className="de-insights-panel-title">
              <span className="de-insights-mini-icon">
                <Eye size={18} />
              </span>
              <h3>District Watchlist</h3>
            </div>
          </div>
          <div className="de-insights-watchlist">
            {watchlist.map((row) => (
              <div key={row.rank} className={`de-insights-watch-row ${row.tone}`}>
                <span className="de-insights-rank">{row.rank}</span>
                <span>{row.label}</span>
                <b className={`de-insights-badge ${row.badgeTone}`}>{row.value}</b>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="de-insights-action-plan">
        <InterventionActionPlan
          aiContent={aiContent}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <section className="de-insights-panel de-insights-timeline-panel">
        <div className="de-insights-panel-head">
          <div className="de-insights-panel-title">
            <span className="de-insights-mini-icon">
              <Clock size={18} />
            </span>
            <h3>Action Timeline & Ownership</h3>
          </div>
        </div>

        <div className="de-insights-timeline">
          {timeline.map((step, idx) => (
            <article key={step.step ?? idx} className="de-insights-timeline-step">
              <span className="de-insights-step-no">{step.step ?? String(idx + 1).padStart(2, "0")}</span>
              <div>
                <h4>{step.title}</h4>
                <p>{step.text ?? step.p ?? step.description}</p>
                <span className="de-insights-owner">♙ {step.owner}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
